// slices/chat/blockThreadScope.ts
//
// The first turn of a QBank block's one chat (ORA-1285) asks the server for the
// block's thread: `threadScope: 'qbank_block'` resumes the block thread, or
// creates it on the question on screen. Only a turn with no conversation yet
// needs it; with a `conversationId` the server ignores it anyway, and it is
// left off so the wire says what the turn really is.

import { getChatBlockScopeEntry } from '../../chatContext/chatBlockThread';
import type { ChatTurnRequest } from '../../types/chatStream';
import { draftConversationId, isDraftConversationId } from './chatSlice';

const asRecord = (value: unknown): Record<string, unknown> | null =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

/** The block a turn's envelope was sent from, when a question is on screen. */
const envelopeBlockId = (request: ChatTurnRequest): string | null => {
  const activity = asRecord(request.envelope?.activity);
  const anchor = asRecord(request.envelope?.anchor);

  if (activity?.kind !== 'qbank_block' || anchor?.type !== 'question')
    return null;

  return typeof activity.blockId === 'string' ? activity.blockId : null;
};

export const withBlockThreadScope = (
  request: ChatTurnRequest
): ChatTurnRequest => {
  const { conversationId } = request;
  if (conversationId && !isDraftConversationId(conversationId)) return request;

  const blockId = envelopeBlockId(request);
  if (!blockId || getChatBlockScopeEntry(blockId) !== 'block') return request;

  return { ...request, threadScope: 'qbank_block' };
};

/**
 * The conversation a `threadScope` turn writes to is that block's chat from its
 * first message, so the thread can read as one before anything is re-read.
 * Null for any other turn.
 */
export const blockThreadOfTurn = (
  request: ChatTurnRequest,
  turnId: string
): { id: string; questionBlockId: string } | null => {
  const blockId = request.threadScope ? envelopeBlockId(request) : null;
  if (!blockId) return null;

  return {
    id: request.conversationId ?? draftConversationId(turnId),
    questionBlockId: blockId,
  };
};
